import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { showToast } from '../utils/toast';

const plans = [
    { id: 'startup', name: 'Startup (Grátis)' },
    { id: 'starter', name: 'Starter (R$49,99/mês)' },
    { id: 'advanced', name: 'Advanced (R$99,99/mês)' },
    { id: 'custom', name: 'Personalizado' }
];

function AddUserModal({ show, onClose, onUserAdded }) {
    // Estados do formulário
    const [email, setEmail] = useState('');
    const [plan, setPlan] = useState('startup');
    const [maxTenants, setMaxTenants] = useState(10);

    // Estados de controle do fluxo
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tempPassword, setTempPassword] = useState('');

    // Limpa o estado interno quando o modal é fechado
    const handleClose = () => {
        setEmail('');
        setPlan('startup');
        setMaxTenants(10);
        setTempPassword('');
        setIsSubmitting(false);
        onClose();
    };
    
    // Garante a limpeza do estado se o modal for fechado abruptamente
    useEffect(() => {
        if (!show) {
            handleClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const payload = { email, plan };

        if (plan === 'custom') {
            const tenantLimit = parseInt(maxTenants, 10);
            if (isNaN(tenantLimit) || tenantLimit <= 0) {
                showToast('warning', 'Para o plano Personalizado, forneça um limite de tenants válido.');
                setIsSubmitting(false);
                return;
            }
            payload.maxTenants = tenantLimit;
        }

        try {
            const functions = getFunctions();
            const createNewUser = httpsCallable(functions, 'create_new_msp_user');
            const result = await createNewUser(payload);

            // Armazena a senha para exibir na UI de sucesso
            setTempPassword(result.data.tempPassword); 
            onUserAdded(); // Avisa o componente pai para atualizar a lista

        } catch (error) {
            showToast('error', `Não foi possível criar o usuário: ${error.message}`);
            // Se der erro, não limpamos o formulário para que o usuário possa corrigir
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopyAndClose = () => {
        if (tempPassword) {
            navigator.clipboard.writeText(tempPassword);
            showToast('success', 'Senha copiada para a área de transferência!');
        }
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleClose} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    {tempPassword ? 'Usuário Criado com Sucesso!' : 'Adicionar Novo Usuário MSP'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {tempPassword ? (
                    <div>
                        <p>O usuário <strong>{email}</strong> foi criado com sucesso.</p>
                        <p className="mt-3"><strong>Senha Temporária:</strong></p>
                        <InputGroup>
                            <Form.Control
                                value={tempPassword}
                                readOnly
                                className="text-center"
                            />
                        </InputGroup>
                        <p className="mt-2 small text-danger">
                            Copie esta senha e envie para o usuário de forma segura. Ele deverá alterá-la no primeiro login.
                        </p>
                    </div>
                ) : (
                    <Form id="addUserForm" onSubmit={handleSubmit}>
                        <div className="form-floating mb-3">
                            <input type="email" className="form-control" id="newUserEmail" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@provedor.com" required />
                            <label htmlFor="newUserEmail">E-mail do Novo Usuário</label>
                        </div>
                        <div className="form-floating mb-3">
                            <select className="form-select" id="userPlan" value={plan} onChange={(e) => setPlan(e.target.value)}>
                                {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                            <label htmlFor="userPlan">Plano de Assinatura</label>
                        </div>

                        {plan === 'custom' && (
                            <div className="form-floating">
                                <input
                                    type="number"
                                    className="form-control"
                                    id="maxTenants"
                                    value={maxTenants}
                                    onChange={(e) => setMaxTenants(e.target.value)}
                                    placeholder="Nº de Tenants"
                                    min="1"
                                    required
                                />
                                <label htmlFor="maxTenants">Limite de Tenants</label>
                            </div>
                        )}
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                {tempPassword ? (
                    <Button variant="primary" onClick={handleCopyAndClose}>
                        Copiar Senha e Fechar
                    </Button>
                ) : (
                    <>
                        <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" form="addUserForm" disabled={isSubmitting}>
                            {isSubmitting ? 'Criando...' : 'Criar Usuário'}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}

export default AddUserModal;